import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Share2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { linkedInPosts } from '@/data/blogPosts';

export const LinkedInCarousel: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320; // Width of one LinkedIn embed + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const getLinkedInUrl = (embedUrl: string) => {
    // Extract the post ID from the embed URL and construct the regular LinkedIn URL
    const match = embedUrl.match(/urn:li:share:(\d+)/);
    if (match) {
      return `https://www.linkedin.com/feed/update/urn:li:share:${match[1]}/`;
    }
    return embedUrl.replace('/embed', '');
  };

  if (linkedInPosts.length === 0) return null;

  return (
    <section className="py-8 px-4 bg-muted/10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-primary" />
            <h2 className="text-lg font-semibold">Latest from LinkedIn</h2>
          </div>
          {linkedInPosts.length > 1 && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => scroll('left')}
                className="h-8 w-8"
                aria-label="Previous LinkedIn post"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => scroll('right')}
                className="h-8 w-8"
                aria-label="Next LinkedIn post"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className="relative">
          <div 
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {linkedInPosts.map((post) => (
              <article 
                key={post.id} 
                className="flex-shrink-0 bg-card rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border group max-w-[320px]"
                itemScope
                itemType="https://schema.org/SocialMediaPosting"
              >
                {/* Category Badge */}
                {post.category && (
                  <div className="px-3 pt-3 pb-1">
                    <Badge variant="secondary" className="text-xs">
                      {post.category}
                    </Badge>
                  </div>
                )}
                
                {/* Embed Container */}
                <div className="relative">
                  <iframe 
                    src={post.embedUrl}
                    height={280}
                    width={320}
                    frameBorder="0"
                    allowFullScreen
                    title={`LinkedIn post: ${post.title}`}
                    className="max-w-full pointer-events-none"
                    loading="lazy"
                  />
                  <a
                    href={getLinkedInUrl(post.embedUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors"
                    aria-label={`View ${post.title} on LinkedIn`}
                    itemProp="url"
                  >
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                      <ExternalLink className="h-5 w-5 text-primary" />
                    </div>
                  </a>
                </div>
                
                {/* Content Section with SEO Text */}
                <div className="p-3 border-t bg-background space-y-2">
                  {post.title && (
                    <h3 className="font-semibold text-sm line-clamp-1" itemProp="headline">
                      {post.title}
                    </h3>
                  )}
                  
                  {/* Summary - Key for SEO */}
                  {post.summary && (
                    <p className="text-xs text-foreground/80 leading-relaxed line-clamp-2" itemProp="description">
                      {post.summary}
                    </p>
                  )}
                  
                  {/* Keywords as tags */}
                  {post.keywords && post.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {post.keywords.slice(0, 3).map((keyword, index) => (
                        <span 
                          key={index}
                          className="text-[10px] px-2 py-0.5 bg-muted rounded-full text-muted-foreground"
                          itemProp="keywords"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Date */}
                  <time 
                    className="text-xs text-muted-foreground block pt-1" 
                    dateTime={post.date}
                    itemProp="datePublished"
                  >
                    {new Date(post.date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </time>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};